import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  demoApi,
  DemoCaseListItem,
  DemoRunDetail,
} from '../api/demo';
import { authApi } from '../api/auth';
import { AppLayout } from '../components/AppLayout';
import { DemoRunStatus } from '../components/DemoRunStatus';
import { DemoExportButton } from '../components/DemoExportButton';
import {
  FlaskConical,
  RefreshCw,
  AlertTriangle,
  ShieldCheck,
  FileWarning,
  ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('it-IT');
}

function shortCommit(value: string | null): string {
  if (!value) return '—';
  return value.slice(0, 7);
}

function countCasesByStatus(cases: DemoCaseListItem[], status: string): number {
  return cases.filter((item) => item.status === status).length;
}

/**
 * Chiavi del sommario che descrivono l'esecuzione tecnica, non una metrica
 * clinica: restano fuori dalla sezione delle metriche provvisorie.
 */
const TECHNICAL_SUMMARY_KEYS = new Set([
  'cases',
  'total',
  'processed',
  'passed',
  'blocked',
  'failed',
  'skipped',
]);

interface ProvisionalMetric {
  key: string;
  label: string;
  value: number;
}

/**
 * Metriche cliniche disponibili per il run.
 *
 * Il read-model del run espone solo conteggi di esito e un sommario libero.
 * I conteggi diventano le metriche provvisorie del run; eventuali valori
 * numerici extra nel sommario (dove finiranno gli aggregati di metrica) sono
 * inclusi senza inventare campi. Se non c'e' alcun numero, non c'e' metrica.
 */
function provisionalMetrics(run: DemoRunDetail): ProvisionalMetric[] {
  const summary = run.summary ?? {};
  const total =
    typeof summary.cases === 'number'
      ? summary.cases
      : typeof summary.total === 'number'
        ? summary.total
        : typeof summary.processed === 'number'
          ? summary.processed
          : run.cases.length;

  if (total <= 0 && run.cases.length === 0) {
    return [];
  }

  const metrics: ProvisionalMetric[] = [
    {
      key: 'passed',
      label: 'Passati',
      value:
        typeof summary.passed === 'number'
          ? summary.passed
          : countCasesByStatus(run.cases, 'passed'),
    },
    {
      key: 'blocked',
      label: 'Bloccati',
      value:
        typeof summary.blocked === 'number'
          ? summary.blocked
          : countCasesByStatus(run.cases, 'blocked'),
    },
    {
      key: 'failed',
      label: 'Falliti',
      value:
        typeof summary.failed === 'number'
          ? summary.failed
          : countCasesByStatus(run.cases, 'failed'),
    },
  ];

  for (const [key, value] of Object.entries(summary)) {
    if (TECHNICAL_SUMMARY_KEYS.has(key)) continue;
    if (typeof value === 'number') {
      metrics.push({ key, label: key.replace(/_/g, ' '), value });
    }
  }

  return metrics;
}

function failureTaxonomy(cases: DemoCaseListItem[]): Array<{ code: string; count: number }> {
  const counts = new Map<string, number>();
  for (const item of cases) {
    if (!item.failure_code) continue;
    counts.set(item.failure_code, (counts.get(item.failure_code) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Dettaglio di un run demo (route `/demo/runs/:runId`).
 *
 * Tiene deliberatamente separati integrita' tecnica, metriche cliniche
 * provvisorie, tassonomia dei fallimenti e tabella dei casi. Le metriche sono
 * sempre etichettate `Provvisorie` finche' il run contiene numeri; senza
 * metriche l'etichetta non compare su valori vuoti.
 */
export function DemoRunPage() {
  const { runId } = useParams<{ runId: string }>();
  const token = authApi.getToken();

  const { data: run, isLoading, error, refetch } = useQuery({
    queryKey: ['demo-run', runId],
    queryFn: () => demoApi.getRun(runId as string),
    enabled: token !== null && Boolean(runId),
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const metrics = run ? provisionalMetrics(run) : [];
  const failures = run ? failureTaxonomy(run.cases) : [];

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Demo casi reali', href: '/demo' },
        { label: run?.dataset_name ?? runId ?? 'Run' },
      ]}
      logoIcon={<FlaskConical className="size-4 text-primary" />}
      topbarActions={
        <div className="flex items-center gap-2">
          <DemoExportButton runId={runId as string} />
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="size-3.5" />
            Aggiorna
          </Button>
        </div>
      }
      mainClassName="mail-utility-page"
    >
      <div className="max-w-6xl mx-auto space-y-5">
        {isLoading ? (
          <div className="text-center text-muted-foreground py-8">Caricamento...</div>
        ) : error ? (
          <Card className="border-danger/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-8 text-danger" />
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Errore nel caricamento del run
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {(error as Error).message || 'Errore di connessione al server.'}
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                    <RefreshCw className="size-3.5" />
                    Riprova
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : run ? (
          <>
            {/* Integrita' tecnica */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-muted-foreground" />
                    Integrità tecnica
                  </h2>
                  <DemoRunStatus status={run.status} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
                  <div>
                    <div className="text-xs text-muted-foreground">Dataset</div>
                    <div className="text-sm font-medium text-foreground">{run.dataset_name}</div>
                    <div className="text-xs text-muted-foreground">
                      v{run.dataset_version} · {run.dataset_kind}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Commit</div>
                    <div className="text-sm font-mono text-foreground">
                      {shortCommit(run.git_commit)}
                    </div>
                    {run.prompt_version && (
                      <div className="text-xs text-muted-foreground">
                        prompt {run.prompt_version}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Seed</div>
                    <div className="text-sm text-foreground">{run.seed ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Avviato</div>
                    <div className="text-sm text-foreground">{formatTimestamp(run.started_at)}</div>
                    <div className="text-xs text-muted-foreground">
                      concluso {formatTimestamp(run.completed_at)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Metriche cliniche provvisorie */}
            {metrics.length > 0 ? (
              <Card data-testid="demo-provisional-metrics">
                <CardContent className="p-0">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      <ClipboardList className="size-4 text-muted-foreground" />
                      Metriche cliniche provvisorie
                    </h2>
                    <Badge
                      variant="outline"
                      className="bg-warning/10 text-warning border-warning/20"
                    >
                      Provvisorie
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
                    {metrics.map((metric) => (
                      <div key={metric.key}>
                        <div className="text-xs text-muted-foreground capitalize">
                          {metric.label}
                        </div>
                        <div className="text-xl font-bold text-foreground">{metric.value}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="demo-provisional-metrics-empty">
                <CardContent className="p-6 text-center text-muted-foreground">
                  Nessuna metrica clinica disponibile per questo run
                </CardContent>
              </Card>
            )}

            {/* Tassonomia dei fallimenti */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-muted/30 border-b border-border">
                  <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <FileWarning className="size-4 text-muted-foreground" />
                    Tassonomia dei fallimenti
                  </h2>
                </div>
                {failures.length > 0 ? (
                  <div className="p-6 space-y-2">
                    {failures.map((failure) => (
                      <div
                        key={failure.code}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="font-mono text-foreground">{failure.code}</span>
                        <span className="text-muted-foreground">{failure.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    Nessun fallimento registrato
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabella dei casi */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-muted/30 border-b border-border">
                  <h2 className="text-sm font-semibold text-foreground">Casi del run</h2>
                </div>
                {run.cases.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Caso</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Fallimento</TableHead>
                        <TableHead>Review</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {run.cases.map((item) => (
                        <TableRow key={item.id} data-testid={`demo-case-row-${item.id}`}>
                          <TableCell>
                            <Link
                              to={`/demo/runs/${run.id}/cases/${item.id}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {item.case_key}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <DemoRunStatus status={item.status} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.current_stage ?? '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-danger">
                            {item.failure_code ?? '—'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.review_status
                              ? `${item.review_status} · rev ${item.review_revision ?? 0}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    Nessun caso nel run
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Nessun dato disponibile per questo run
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
